/**
 * WT1 - the walk-through photo tags. ⛔ A HAND MIRROR of `backend/src/services/tourTags.ts`: the
 * frontend may never import backend source (it passes locally and fails the Vercel build -
 * `noBackendImports.test.ts`). `backend/src/services/tourTags.mirror.test.ts` reads this file as text
 * and fails when it disagrees with the backend list or the migration's `subject` CHECK.
 *
 * ⭐ Tags follow the property type (his review, 2026-09-23): seven on every tour plus five by type.
 */

export const TAG_SETS = ['industrial', 'retail', 'office'] as const;
export type TagSet = (typeof TAG_SETS)[number];

export const TAG_SET_LABELS: Record<TagSet, string> = {
  industrial: 'Industrial',
  retail: 'Retail / restaurant',
  office: 'Office',
};

export const COMMON_TAGS = ['exterior', 'parking', 'roof', 'power', 'sprinklers', 'restrooms', 'issue'] as const;

export const TYPE_TAGS: Record<TagSet, readonly string[]> = {
  industrial: ['warehouse', 'office', 'dock', 'yard', 'mezzanine'],
  retail: ['storefront', 'sales_floor', 'kitchen', 'hvac', 'drive_thru'],
  office: ['lobby', 'suites', 'hvac', 'elevators', 'amenities'],
};

export const TAG_LABELS = {
  exterior: 'Exterior',
  parking: 'Parking',
  roof: 'Roof',
  power: 'Power & utilities',
  sprinklers: 'Sprinklers & fire',
  restrooms: 'Restrooms',
  issue: 'Issue',
  warehouse: 'Warehouse',
  office: 'Office',
  dock: 'Dock',
  yard: 'Yard',
  mezzanine: 'Mezzanine',
  storefront: 'Storefront & signage',
  sales_floor: 'Sales floor / dining',
  kitchen: 'Kitchen & back of house',
  hvac: 'HVAC',
  drive_thru: 'Drive-thru',
  lobby: 'Lobby & common areas',
  suites: 'Suites',
  elevators: 'Elevators',
  amenities: 'Amenities',
} as const;

export type TagCode = keyof typeof TAG_LABELS;

export const isTagCode = (value: unknown): value is TagCode =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(TAG_LABELS, value);

export const isTagSet = (value: unknown): value is TagSet =>
  typeof value === 'string' && (TAG_SETS as readonly string[]).includes(value);

/** Retail and office have their own set; everything else, including a blank type, reads industrial. */
export const tagSetFor = (propertyType: string | null | undefined): TagSet => {
  const t = (propertyType ?? '').trim().toLowerCase();
  if (t === 'retail') return 'retail';
  if (t === 'office') return 'office';
  return 'industrial';
};

/** The chips in the order the phone shows them: the five type tags first, the common seven after. */
export const chipsFor = (set: TagSet): TagCode[] => [
  ...(TYPE_TAGS[set] as TagCode[]),
  ...(COMMON_TAGS as readonly TagCode[]),
];

export const tagLabel = (code: string): string =>
  isTagCode(code) ? TAG_LABELS[code] : code;
